/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file cms-selector.js
 * @description Redux selectors for accessing cms state data
 */

import { createSelector } from "@reduxjs/toolkit";

import {
  CMS_CONFIG_TYPE_ACCOUNTS,
  CMS_CONFIG_TYPE_APPEARANCE,
  CMS_CONFIG_TYPE_BETRADAR_VIRTUAL,
  CMS_CONFIG_TYPE_BETTING,
  CMS_CONFIG_TYPE_BRAND_DETAILS,
  CMS_CONFIG_TYPE_BRAND_LOGOS,
  CMS_CONFIG_TYPE_KIRON_VIRTUAL,
  CMS_CONFIG_TYPE_RESULTS,
  CMS_CONFIG_TYPE_SPORTS_BOOK,
} from "constants/cms-config-types";

/**
 * @function getCmsSelector
 * @description Selector that returns the cms data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsSelector = (state) => state.cms || {};

/**
 * @function getCmsConfigSelector
 * @description Selector that returns the cms config data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigSelector = (state) => state.cms?.config;

/**
 * @function getCmsConfigError
 * @description Selector that returns the cms config error data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigError = (state) => state.cms?.error;

/**
 * @function getCmsConfigErrorCode
 * @description Selector that returns the cms config error code data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigErrorCode = (state) => state.cms?.errorCode;

/**
 * @function getCmsOriginId
 * @description Selector that returns the cms origin id data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsOriginId = (state) => state.cms?.originId;

/**
 * @function getCmsLineId
 * @description Selector that returns the cms line id data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsLineId = (state) => state.cms?.lineId;

/**
 * @function getCmsConfigIsLoading
 * @description Selector that returns the cms config is loading data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigIsLoading = createSelector(
  (state) => state.cms?.loading,
  (loading) => loading,
);

const getCmsConfigByType = (type) =>
  createSelector(
    (state) => state.cms?.config?.siteConfigs,
    (siteConfigs) => siteConfigs?.find(({ configType }) => configType === type),
  );

export const getCmsConfigAccounts = getCmsConfigByType(CMS_CONFIG_TYPE_ACCOUNTS);

export const getCmsConfigAppearance = getCmsConfigByType(CMS_CONFIG_TYPE_APPEARANCE);

export const getCmsConfigBetradarVirtual = getCmsConfigByType(CMS_CONFIG_TYPE_BETRADAR_VIRTUAL);

export const getCmsConfigBetting = getCmsConfigByType(CMS_CONFIG_TYPE_BETTING);

export const getCmsConfigBrandDetails = getCmsConfigByType(CMS_CONFIG_TYPE_BRAND_DETAILS);

export const getCmsConfigKironVirtual = getCmsConfigByType(CMS_CONFIG_TYPE_KIRON_VIRTUAL);

export const getCmsConfigResults = getCmsConfigByType(CMS_CONFIG_TYPE_RESULTS);

export const getCmsConfigSportsBook = getCmsConfigByType(CMS_CONFIG_TYPE_SPORTS_BOOK);

/**
 * @function getCmsConfigBrandLogos
 * @description Selector that returns the cms config brand logos data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigBrandLogos = createSelector(
  getCmsConfigByType(CMS_CONFIG_TYPE_BRAND_LOGOS),
  (siteConfig) => siteConfig?.data || {},
);

/**
 * @function getCmsConfigBrandName
 * @description Selector that returns the cms config brand name data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigBrandName = createSelector(
  getCmsConfigBrandDetails,
  (brandDetails) => brandDetails?.data?.brandName,
);

/**
 * @function getCmsConfigIframeMode
 * @description Selector that returns the cms config iframe mode data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigIframeMode = createSelector(
  getCmsConfigBrandDetails,
  (brandDetails) => brandDetails?.data?.iframeMode,
);

/**
 * @function getCmsConfigVaixMode
 * @description Selector that returns the cms config vaix mode data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigVaixMode = createSelector(
  getCmsConfigBrandDetails,
  (brandDetails) => brandDetails?.data?.vaix,
);

/**
 * @function getCmsConfigCustomBetEnabled
 * @description Selector that returns the cms config custom bet enabled data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigCustomBetEnabled = createSelector(
  getCmsConfigBrandDetails,
  (brandDetails) => !!brandDetails?.data?.customBet,
);

/**
 * @function getCmsConfigSingleWalletMode
 * @description Selector that returns the cms config single wallet mode data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigSingleWalletMode = createSelector(
  getCmsConfigBrandDetails,
  (brandDetails) => brandDetails?.data?.singleWalletMode,
);

/**
 * @function getCmsConfigCashoutMode
 * @description Selector that returns the cms config cashout mode data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigCashoutMode = createSelector(
  getCmsConfigBrandDetails,
  (brandDetails) => brandDetails?.data?.cashoutMode,
);

/**
 * @function getCmsBrandDetailsCountries
 * @description Selector that returns the cms brand details countries data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsBrandDetailsCountries = createSelector(
  getCmsConfigBrandDetails,
  (state) => state.country?.countries,
  (configBrandDetails, countriesList) => {
    if (!configBrandDetails || !countriesList) {
      return [];
    }
    const {
      data: { countries },
    } = configBrandDetails;
    if (!countries) {
      return [];
    }

    return countries.reduce((acc, country) => {
      const countryItem = countriesList.find(({ isoCode2 }) => isoCode2 === country);

      return countryItem
        ? [
            ...acc,
            {
              key: country,
              label: countryItem.description,
              value: country,
            },
          ]
        : acc;
    }, []);
  },
);

/**
 * @function getCmsBrandDetailsCurrencies
 * @description Selector that returns the cms brand details currencies data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsBrandDetailsCurrencies = createSelector(
  getCmsConfigBrandDetails,
  (state) => state.currency?.currencies,
  (configBrandDetails, currenciesList) => {
    if (!configBrandDetails || !currenciesList) {
      return [];
    }
    const {
      data: { currencies },
    } = configBrandDetails;
    if (!currencies) {
      return [];
    }

    return currencies.reduce((acc, currency) => {
      const currencyItem = currenciesList.find(({ isoCode }) => isoCode === currency);

      return currencyItem
        ? [
            ...acc,
            {
              key: currency,
              label: currencyItem.description,
              value: currency,
            },
          ]
        : acc;
    }, []);
  },
);

/**
 * @function getCmsBrandDetailsLanguages
 * @description Selector that returns the cms brand details languages data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsBrandDetailsLanguages = createSelector(
  getCmsConfigBrandDetails,
  (state) => state.language?.languages,
  (configBrandDetails, languagesList) => {
    if (!configBrandDetails || !languagesList) {
      return [];
    }
    const {
      data: { languages },
    } = configBrandDetails;
    if (!languages) {
      return [];
    }

    return languages.reduce((acc, language) => {
      const languageItem = languagesList.find(({ isoCode2 }) => isoCode2 === language);

      return languageItem
        ? [
            ...acc,
            {
              key: language,
              label: languageItem.description,
              value: language,
            },
          ]
        : acc;
    }, []);
  },
);

/**
 * @function getCmsConfigAppearanceMobileDashboardPreferences
 * @description Selector that returns the cms config appearance mobile dashboard preferences data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigAppearanceMobileDashboardPreferences = createSelector(
  getCmsConfigByType(CMS_CONFIG_TYPE_APPEARANCE),
  (cmsConfig) => {
    if (cmsConfig?.data?.mobileDashboardPreferences) {
      return [...cmsConfig?.data?.mobileDashboardPreferences].sort((a, b) => {
        let aValue;
        let bValue;
        switch (a) {
          case "FEATURED":
            aValue = 0;
            break;
          case "NEXT":
            aValue = 100;
            break;
          case "LIVE":
            aValue = 1000;
            break;
          default:
            aValue = 10000;
            break;
        }
        switch (b) {
          case "FEATURED":
            bValue = 0;
            break;
          case "NEXT":
            bValue = 100;
            break;
          case "LIVE":
            bValue = 1000;
            break;
          default:
            bValue = 10000;
            break;
        }

        return aValue - bValue;
      });
    }

    return [];
  },
);

/**
 * @function getCmsConfigKironVirtualVideoByFeedCode
 * @description Selector that returns the cms config kiron virtual video by feed code data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigKironVirtualVideoByFeedCode = createSelector(
  getCmsConfigKironVirtual,
  (_, feedCode) => feedCode,
  (config, feedCode) => {
    if (!config) {
      return undefined;
    }
    const {
      data: { videoURLs },
    } = config || { data: {} };

    return videoURLs[feedCode];
  },
);

/**
 * @function getCmsConfigBettingStakeLimits
 * @description Selector that returns the cms config betting stake limits data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigBettingStakeLimits = createSelector(getCmsConfigBetting, (config) => {
  if (!config?.data?.stakeLimits) {
    return undefined;
  }

  return config.data.stakeLimits;
});

/**
 * @function getCmsConfigBettingSingleStakeLimits
 * @description Selector that returns the cms config betting single stake limits data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigBettingSingleStakeLimits = createSelector(getCmsConfigBettingStakeLimits, (limits) => {
  if (!limits?.singles) {
    return undefined;
  }

  return limits.singles;
});

/**
 * @function getCmsConfigBettingMultipleStakeLimits
 * @description Selector that returns the cms config betting multiple stake limits data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigBettingMultipleStakeLimits = createSelector(getCmsConfigBettingStakeLimits, (limits) => {
  if (!limits?.accumulators) {
    return undefined;
  }

  return limits.accumulators;
});

/**
 * @function getCmsConfigBettingSpecialStakeLimits
 * @description Selector that returns the cms config betting special stake limits data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigBettingSpecialStakeLimits = createSelector(getCmsConfigBettingStakeLimits, (limits) => {
  if (!limits?.specials) {
    return undefined;
  }

  return limits.specials;
});

/**
 * @function isCmsConfigResultEnabled
 * @description Selector that returns the is cms config result enabled data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const isCmsConfigResultEnabled = createSelector(getCmsConfigResults, (resultDetails) => resultDetails?.data?.on);

/**
 * @function isCmsConfigResultTypeNative
 * @description Selector that returns the is cms config result type native data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const isCmsConfigResultTypeNative = createSelector(
  getCmsConfigResults,
  (resultDetails) => resultDetails?.data?.type === "NATIVE",
);

/**
 * @function isCmsConfigResultTypeBetradar
 * @description Selector that returns the is cms config result type betradar data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const isCmsConfigResultTypeBetradar = createSelector(
  getCmsConfigResults,
  (resultDetails) => resultDetails?.data?.type === "BETRADAR",
);

/**
 * @function getCmsConfigResultBetradarUrl
 * @description Selector that returns the cms config result betradar url data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getCmsConfigResultBetradarUrl = createSelector(
  getCmsConfigResults,
  (resultDetails) => resultDetails?.data?.url,
);
